import React from 'react';
import { Phone, Mail, MessageSquare, Printer, Facebook, Instagram, Youtube } from 'lucide-react';
import { TiktokIcon } from '../../external/tiktok-icon/TiktokIcon';
import { ContactDetails, SocialMediaProfilesTypes } from '../../../types';
import { buildSmsHref, buildTelHref } from '../../../lib/utils/phone';

export type ContactUsProps = {
    contactDetails: ContactDetails;
    socialMediaProfiles?: SocialMediaProfilesTypes;
    hideSMS?: boolean;
    showSocials?: boolean;
    showContacts?: boolean;
    hidePhone?: boolean;
    hideLabels?: boolean;
    darkMode?: boolean;
    customColor?: string;
    contactTitle?:string;
    socialTitle?:string;
    hideSocialHandles?:boolean;
};

const ContactUsTiles = ({
                            contactDetails,
                            socialMediaProfiles = {},
                            contactTitle,
                            socialTitle,
                            hideSMS = false,
                            showSocials = false,
                            hideSocialHandles = true,
                            showContacts = true,
                            hidePhone = false,
                            hideLabels = false,
                            darkMode = false,
                            customColor,
                        }: ContactUsProps) => {
    const { contactNumberOne, contactNumberTwo, contactEmail, fax } = contactDetails;

    const {
        facebookHandle,
        tikTokHandle,
        instaHandle,
        youTubeHandle,
        facebookLink = 'https://www.facebook.com/',
        tikTokLink = 'https://www.tiktok.com/@',
        instaLink = 'https://www.instagram.com/',
        youTubeLink = 'https://www.youtube.com/user/',
    } = socialMediaProfiles;

    const contentColor = customColor ?? (darkMode ? '#FFFFFF' : '#1F2937'); // fallback to Tailwind gray-800

    // Contact block
    const renderContactBlock = (
        Icon: React.ElementType,
        label: string,
        buildHref: (value: string) => string,
        content: string,
        secondaryContent?: string
    ) => {
        if (!content) return null;

        return (
            <div className="flex items-center gap-3 mb-4">
                <Icon style={{ color: contentColor }} className="w-6 h-6 flex-shrink-0" />
                <div style={{ color: contentColor }} className="text-sm">
                    <a href={buildHref(content)} title={label} className="hover:underline">
                        {!hideLabels && `${label}: `} {content}
                    </a>
                    {secondaryContent && (
                        <span>
                            {' '}OR <a href={buildHref(secondaryContent)} className="hover:underline">{secondaryContent}</a>
                        </span>
                    )}
                </div>
            </div>
        );
    };

    // Social block
    const renderSocialBlock = (
        Icon: React.ElementType,
        label: string,
        link: string,
        handle?: string,
        contentColor?:string,
    ) => {
        if (!handle) return null;

        return (
            <div className="flex items-center gap-3 mb-4">
                <Icon style={{ color: contentColor }} color={contentColor} className="w-6 h-6 flex-shrink-0" />
                <a
                    href={`${link}${handle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: contentColor }}
                    className="text-sm hover:underline"
                >
                    {label} {!hideSocialHandles && (<> - {handle}</>)}
                </a>
            </div>
        );
    };

    return (
        <div className="flex flex-col  items-start justify-between gap-5">

            {!!showContacts && (
                <div>
                    {!!contactTitle?.length && (<h2 className="text-lg font-semibold block  mb-3">
                        {contactTitle}
                    </h2>)}
                    <div className="flex flex-col">
                        {!hidePhone && contactNumberOne &&
                            renderContactBlock(Phone, 'Phone', (value) => buildTelHref(value) || `tel:${value.trim()}`, contactNumberOne, contactNumberTwo)
                        }
                        {!hidePhone && !hideSMS && contactNumberOne &&
                            renderContactBlock(MessageSquare, 'SMS', (value) => buildSmsHref(value) || `sms:${value.trim()}`, contactNumberOne, contactNumberTwo)
                        }
                        {fax && renderContactBlock(Printer, 'FAX', (value) => `fax:${value.trim()}`, fax)}
                        {contactEmail && renderContactBlock(Mail, 'Email', (value) => `mailto:${value.trim()}`, contactEmail)}
                    </div>
                </div>
            )}

            {/* Socials */}
            {showSocials && (
                <div className="pt-5 md:pt-0">
                    {!!socialTitle?.length && (<h2 className="text-lg font-semibold block mb-3">
                        {socialTitle}
                    </h2>)}
                    <div className="flex flex-col">
                        {renderSocialBlock(Facebook, 'Facebook', facebookLink, facebookHandle)}
                        {renderSocialBlock(TiktokIcon , 'TikTok', tikTokLink, tikTokHandle, contentColor)}
                        {renderSocialBlock(Instagram, 'Instagram', instaLink, instaHandle)}
                        {renderSocialBlock(Youtube, 'YouTube', youTubeLink, youTubeHandle)}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactUsTiles;
